import React, { useEffect, useMemo, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import type { Area, Point } from 'react-easy-crop';
import Webcam from 'react-webcam';
import Tesseract from 'tesseract.js';
import { v4 as uuid } from 'uuid';

import useImageUpload from 'src/features/onboarding/hooks/use-image-upload';
import { ImageUploadActionType } from 'src/features/onboarding/types/image-upload-reducer-actions';
import {
  CameraButton,
  CloseIconWhite,
  RotateLeft,
  RotateRight,
  ZoomIn,
  ZoomOut,
} from 'src/assets/images';
import getCroppedImg from 'src/features/onboarding/context/crop-image';
import { ImageUploadJourney } from 'src/features/onboarding/context/image-upload-context';
import {
  DocumentWithOptionalBody,
  OnboardingDocumentTypeEnum,
} from 'src/features/onboarding/pages/upload-documents/upload-documents';
import { CaptureImage } from '../capture-image/capture-image';
import { FullPageLoader } from 'src/components';
import { Environment } from 'src/Environment';
import { removeBase64Prefix } from 'src/helpers/formatting';
import { NotifyPosition, Toast, notify } from 'src/features/common/components/toast/toast';
import { useIsMobile } from 'src/features/common/hooks/use-is-mobile';
import { FileTypes } from 'src/features/common/components/file-upload/file-upload';
import { fileTypes } from '../../helpers/file-upload-helpers';
import 'react-toastify/dist/ReactToastify.css';

const MAX_ZOOM = 3;
const ZOOM_STEP = 0.05;
const MAX_ROTATION = 360;
const ASPECT = 8 / 5;

export default function IdGuideModal() {
  const webcamRef = useRef<Webcam>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { imageUploadState, imageUploadDispatch } = useImageUpload();
  const isMobile = useIsMobile();

  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [rotation, setRotation] = useState<number>(0);
  const [zoom, setZoom] = useState<number>(1);
  const [src, setSrc] = useState<string | undefined>(undefined);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>();
  const [imageUploaded, setImageUploaded] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [cameraReady, setCameraReady] = useState<boolean>(false);

  const dialogRef = useRef<HTMLDialogElement>(null);

  const idNumberCheckEnabled = useMemo(
    (): boolean => !Environment.current.disabledFeatures.idOptimisationIdNumberCheck,
    [],
  );

  const imageJoinEnabled = useMemo(
    (): boolean => !Environment.current.disabledFeatures.imageJoin,
    [],
  );

  const idTypeMap = new Map<OnboardingDocumentTypeEnum, string>([
    [OnboardingDocumentTypeEnum.PASSPORT, 'passport'],
    [OnboardingDocumentTypeEnum.ID_CARD, 'ID card'],
    [OnboardingDocumentTypeEnum.ID_TYPE, 'ID'],
    [OnboardingDocumentTypeEnum.ID_BOOK, 'Green book'],
  ]);

  const bothSidesTitles = [
    {
      title: `${
        imageUploadState.journey === ImageUploadJourney.UPLOAD ? 'Upload' : 'Take'
      } a photo of the front`,
      hint: `${
        imageUploadState.journey === ImageUploadJourney.UPLOAD
          ? `Ensure your ${idTypeMap.get(
              imageUploadState.currentIdDocType,
            )} is in the frame and upload your photo`
          : `Position your ${idTypeMap.get(
              imageUploadState.currentIdDocType,
            )} in the frame and take your photo`
      }`,
      button: 'Take photo',
    },
    {
      title: `${
        imageUploadState.journey === ImageUploadJourney.UPLOAD ? 'Upload' : 'Take'
      } a photo of the back`,
      hint: `Position your ${idTypeMap.get(
        imageUploadState.currentIdDocType,
      )} in the frame and take your photo`,
      button: 'Take photo',
    },
  ];

  const titleText = new Map<ImageUploadJourney, string>([
    [ImageUploadJourney.UPLOAD, 'Upload a photo'],
    [ImageUploadJourney.CAMERA, 'Take a photo'],
  ]);

  const retryButtonText = new Map<ImageUploadJourney, string>([
    [ImageUploadJourney.UPLOAD, 'Retry'],
    [ImageUploadJourney.CAMERA, 'Retake'],
  ]);

  const buttonText = {
    [ImageUploadJourney.UPLOAD]: `Upload a photo`,
    [ImageUploadJourney.CAMERA]: `Take photo`,
  };

  const onCropComplete = (_: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const renameDoc = (name: string, index: number) => {
    const label = {
      1: 'Front',
      2: 'Back',
    };

    // check if there is already a doc uploaded
    const uploadedDoc = imageUploadState.documents.find(
      (doc) => doc.idNumber === imageUploadState.currentIdNumber,
    );

    if (uploadedDoc) {
      index = uploadedDoc.documentName?.includes(label[1]) ? 2 : 1;
    }

    const sideLabel = imageUploadState.requestBothSides || uploadedDoc ? `- ${label[index]}` : '';

    return `${name.substring(0, name.indexOf('.'))}${sideLabel}${name.substring(
      name.indexOf('.'),
    )}`;
  };

  const capture = React.useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      imageUploadDispatch({
        type: ImageUploadActionType.INIT_TAKE_PHOTO,
        takePhoto: true,
        imageSrc: imageSrc ? imageSrc : '',
        stepIndex: imageUploadState.stepIndex,
      });
    }
  }, [webcamRef, imageUploadState.stepIndex]);

  const updateIdFiles = (croppedImage: string): DocumentWithOptionalBody[] => {
    // @ts-ignore
    return imageUploadState.currentDocuments.map((doc, index) => {
      if (index === imageUploadState.stepIndex) {
        const { currentIdNumber: idNumber, currentIdDocType: documentType } = imageUploadState;
        return {
          ...doc,
          documentBody: removeBase64Prefix(croppedImage),
          idNumber,
          documentType,
          documentName: renameDoc(doc?.documentName ?? '', imageUploadState.stepIndex + 1),
        };
      }
      return doc;
    });
  };

  const validateID = async (image: string) => {
    try {
      setIsLoading(true);
      const {
        data: { text },
      } = await Tesseract.recognize(image, 'eng', {});

      return text.trim().includes(imageUploadState.currentIdNumber ?? '');
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Mobile file upload( made to work on ios)
  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles) return;
    const selectedFile = selectedFiles[0];

    // File size too large
    if (imageUploadState?.maxFileSize && selectedFile.size > imageUploadState.maxFileSize) {
      const errorMessage = `File upload failed: Maximum file size of ${
        imageUploadState.maxFileSize > 1000000
          ? `${imageUploadState.maxFileSize / 1024000}MB`
          : `${imageUploadState.maxFileSize / 1024}KB`
      } exceeded.`;
      notify({
        customId: uuid(),
        children: errorMessage,
        autoClose: 5000,
        position: NotifyPosition.top_center,
        type: 'warning',
      });
      return;
    } else if (
      imageUploadState?.allowedFileTypes &&
      !imageUploadState.allowedFileTypes?.includes(selectedFile.type as FileTypes)
    ) {
      // File type is incorrect
      const errorMessage = `File upload failed: Incorrect file type, only ${fileTypes(
        imageUploadState?.allowedFileTypes,
      )} supported.`;
      notify({
        customId: uuid(),
        children: errorMessage,
        autoClose: 5000,
        position: NotifyPosition.top_center,
        type: 'warning',
      });
      return;
    }

    imageUploadDispatch({
      type: ImageUploadActionType.ADD_SELECT_FILES,
      selectedFile: selectedFile,
    });
  };

  const onConfirm = async () => {
    try {
      if (croppedAreaPixels === undefined || src === undefined) return;
      const croppedImage = await getCroppedImg(src, croppedAreaPixels, rotation);

      if (
        imageUploadState.documentType === OnboardingDocumentTypeEnum.ID_TYPE &&
        imageUploadState.stepIndex === 0 &&
        idNumberCheckEnabled
      ) {
        const validId = await validateID(croppedImage ?? '');
        if (!validId) {
          notify({
            customId: uuid(),
            children: `ID number not detected in image, ${
              imageJoinEnabled ? 'Please try again.' : 'Please upload only the front.'
            }`,
            autoClose: 5000,
            position: NotifyPosition.top_center,
            type: 'warning',
          });
          return;
        }
      }

      notify({
        customId: uuid(),
        children: 'Photo added successfully',
        autoClose: 5000,
        position: NotifyPosition.top_center,
        type: 'info',
      });

      imageUploadDispatch({
        type: ImageUploadActionType.SUBMIT_PHOTO,
        documents: updateIdFiles(croppedImage ?? ''),
        stepIndex: imageUploadState.stepIndex,
        openCamera: imageUploadState.journey === ImageUploadJourney.CAMERA,
      });

      if (
        imageUploadState.stepIndex + 1 === bothSidesTitles.length ||
        !imageUploadState.requestBothSides
      ) {
        setImageUploaded((prev) => !prev);
        setSrc(undefined);
        imageUploadDispatch({
          type: ImageUploadActionType.END_TAKE_PHOTO_PROMPT,
        });
        imageUploadDispatch({
          type: ImageUploadActionType.SET_STEP_INDEX,
          stepIndex: 0,
        });
        return;
      }

      imageUploadDispatch({
        type: ImageUploadActionType.SET_STEP_INDEX,
        stepIndex: imageUploadState.stepIndex + 1,
      });

      setImageUploaded(false);
      setSrc(undefined);
    } catch (e) {
      console.error(e);
    }
  };

  const onClick = () => {
    if (fileInputRef && fileInputRef.current && isMobile) {
      fileInputRef.current.click();
      return;
    }
    if (imageUploadState.journey === ImageUploadJourney.UPLOAD) {
      imageUploadDispatch({
        type: ImageUploadActionType.INIT_UPLOAD_PHOTO,
        uploadPhoto: true,
      });
      return;
    }
    capture();
  };

  const onClose = () => {
    imageUploadDispatch({
      type: ImageUploadActionType.CLOSE_TAKE_PHOTO,
      showModal: false,
      documents: [],
    });
  };

  const onRetry = () => {
    if (imageUploadState.journey === ImageUploadJourney.CAMERA) {
      setImageUploaded(false);
      setSrc(undefined);
      imageUploadDispatch({
        type: ImageUploadActionType.RETAKE_PHOTO,
      });
      return;
    }
    imageUploadDispatch({
      type: ImageUploadActionType.RETRY_UPLOAD_PHOTO,
      uploadPhoto: true,
    });
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    event.preventDefault();
    if (event.key == 'Escape') {
      imageUploadDispatch({
        type: ImageUploadActionType.CLOSE_TAKE_PHOTO,
        showModal: false,
        documents: [],
      });
      dialogRef.current?.close();
    }
    return;
  };

  useEffect(() => {
    dialogRef.current?.addEventListener('keydown', handleKeyDown);

    return () => {
      dialogRef.current?.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (
      imageUploadState.showModal &&
      imageUploadState.stepName === ImageUploadActionType.TAKE_PHOTO &&
      dialogRef
    ) {
      dialogRef.current?.showModal();
    }
  }, [imageUploadState.showModal]);

  useEffect(() => {
    if (
      imageUploadState.currentDocuments &&
      imageUploadState.currentDocuments[imageUploadState.stepIndex] &&
      imageUploadState.currentDocuments.length > 0
    ) {
      setImageUploaded(true);
      setSrc(imageUploadState.currentDocuments[imageUploadState.stepIndex].documentUrl);
    }
  }, [imageUploadState]);

  return (
    <dialog
      ref={dialogRef}
      className={`flex flex-col items-center w-full gap-y-2  ${
        isMobile && imageUploadState.journey === ImageUploadJourney.CAMERA
          ? 'backdrop:bg-black-pure bg-black-pure'
          : 'bg-black-opacity-10 backdrop:bg-black backdrop:opacity-95'
      } backdrop:m-0 backdrop:p-0`}
    >
      <Toast />
      <FullPageLoader showLoader={isLoading} />

      {isMobile && imageUploadState.journey === ImageUploadJourney.CAMERA ? (
        <>
          <button className="absolute top-0 z-10 w-7 h-7 right-4" onClick={onClose}>
            <img src={CloseIconWhite} alt="close" />
          </button>

          <div className="flex flex-col items-center bg-black p-1 w-full max-w-[1080px] h-[580px] xs:mt-6 mt-12 z-10 rounded-20">
            <div
              className={`w-full h-full max-w-232 ${
                !!src && imageUploaded
                  ? 'flex flex-col items-center justify-center gap-y-4'
                  : 'relative'
              }`}
            >
              {!!src && imageUploaded ? (
                <>
                  <div className="w-full max-w-[740px] h-[400px] border-2 border-solid border-white relative">
                    <Cropper
                      image={src}
                      crop={crop}
                      rotation={rotation}
                      zoom={zoom}
                      aspect={ASPECT}
                      onCropChange={setCrop}
                      onRotationChange={setRotation}
                      onCropComplete={onCropComplete}
                      onZoomChange={setZoom}
                      showGrid
                    />
                  </div>

                  <div className="flex gap-2">
                    <img
                      className="cursor-pointer w-7 h-7"
                      src={RotateLeft}
                      alt="rotate left"
                      onClick={() => {
                        if (rotation > 0) setRotation(rotation - 1);
                      }}
                    />
                    <img
                      className="cursor-pointer w-7 h-7"
                      src={RotateRight}
                      alt="rotate right"
                      onClick={() => {
                        if (rotation < MAX_ROTATION) setRotation(rotation + 1);
                      }}
                    />
                    <img
                      className="cursor-pointer w-7 h-7"
                      src={ZoomIn}
                      alt="zoom in"
                      onClick={() => {
                        if (zoom < MAX_ZOOM) setZoom(zoom + ZOOM_STEP);
                      }}
                    />
                    <img
                      className="cursor-pointer w-7 h-7"
                      src={ZoomOut}
                      alt="zoom out"
                      onClick={() => {
                        if (zoom > 1) setZoom(zoom - ZOOM_STEP);
                      }}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="absolute inset-0 flex flex-col items-center justify-between w-full h-full p-2 max-h-232">
                    <p className="text-base font-medium text-white-50">TOP</p>
                    <div className="w-full h-full my-4 border border-white border-solid" />
                    <p className="text-base font-medium text-white-50">BOTTOM</p>
                  </div>
                  {imageUploadState.openCamera && (
                    <CaptureImage
                      ref={webcamRef}
                      onCameraReady={() => setCameraReady(true)}
                      className="md:pb-12"
                      cameraFacingMode={isMobile ? 'rear' : 'front'}
                      rounded
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center bg-black p-4 md:p-10 w-full max-w-[1080px]  h-[400px] sm:h-[500px] md:h-[600px] xs:mt-8 mt-12 z-10 relative rounded-20">
          <button className="absolute z-10 w-7 h-7 right-10" onClick={onClose}>
            <img src={CloseIconWhite} alt="close" />
          </button>
          {imageUploadState.openCamera && (
            <div className="absolute w-full max-w-232 h-full max-h-[580px]">
              <CaptureImage
                ref={webcamRef}
                className="md:pb-12"
                onCameraReady={() => setCameraReady(true)}
                cameraFacingMode={isMobile ? 'rear' : 'front'}
                rounded
              />
            </div>
          )}

          {/* Cropper */}
          <div className="flex flex-col items-center justify-center w-full gap-4 mt-8 overflow-hidden md:mt-12 max-w-188">
            {!imageUploaded && <p className="z-10 text-base font-medium text-white-50">TOP</p>}
            <div className="w-full max-w-[740px] h-[400px] border-2 border-solid border-white relative">
              {src && (
                <Cropper
                  image={src}
                  crop={crop}
                  rotation={rotation}
                  zoom={zoom}
                  aspect={ASPECT}
                  onCropChange={setCrop}
                  onRotationChange={setRotation}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                  showGrid
                />
              )}
            </div>

            {!imageUploaded ? (
              <p className="z-10 text-base font-medium text-white-50">BOTTOM</p>
            ) : (
              <div className="flex gap-2">
                <img
                  className="cursor-pointer w-7 h-7"
                  src={RotateLeft}
                  alt="rotate left"
                  onClick={() => {
                    if (rotation > 0) setRotation(rotation - 1);
                  }}
                />
                <img
                  className="cursor-pointer w-7 h-7"
                  src={RotateRight}
                  alt="rotate right"
                  onClick={() => {
                    if (rotation < MAX_ROTATION) setRotation(rotation + 1);
                  }}
                />
                <img
                  className="cursor-pointer w-7 h-7"
                  src={ZoomIn}
                  alt="zoom in"
                  onClick={() => {
                    if (zoom < MAX_ZOOM) setZoom(zoom + ZOOM_STEP);
                  }}
                />
                <img
                  className="cursor-pointer w-7 h-7"
                  src={ZoomOut}
                  alt="zoom out"
                  onClick={() => {
                    if (zoom > 1) setZoom(zoom - ZOOM_STEP);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {isMobile && imageUploadState.journey === ImageUploadJourney.UPLOAD && (
        <input
          type="file"
          id="fileInput"
          className="hidden"
          ref={fileInputRef}
          onChange={handleFileSelection}
          accept={imageUploadState.allowedFileTypes?.join(',')}
        />
      )}

      {!imageUploaded ? (
        <section className="flex flex-col items-center gap-y-4 xs:gap-y-2">
          <h4 className="text-xl font-medium text-center xs:text-3xl text-white-50">
            {imageUploadState.requestBothSides
              ? bothSidesTitles[imageUploadState.stepIndex].title
              : titleText.get(imageUploadState.journey ?? ImageUploadJourney.UPLOAD)}
          </h4>
          <p className="text-center text-white-50">
            {bothSidesTitles[imageUploadState.stepIndex].hint}
          </p>

          {isMobile && imageUploadState.journey === ImageUploadJourney.CAMERA ? (
            <button type="button" onClick={onClick}>
              <img src={CameraButton} className="w-16 h-16 xs:h-22 xs:w-22" />
            </button>
          ) : (
            <button
              className="h-12 text-base font-medium text-center text-black rounded-8 bg-white-50 w-38 hover:bg-grey-100 active:bg-grey-200 disabled:cursor-not-allowed disabled:bg-grey-700"
              type="button"
              onClick={onClick}
              disabled={imageUploadState.journey === ImageUploadJourney.CAMERA && !cameraReady}
            >
              {buttonText[imageUploadState.journey ?? ImageUploadJourney.UPLOAD]}
            </button>
          )}
        </section>
      ) : (
        <section className="flex flex-col items-center gap-4 ">
          <h4 className="text-3xl text-center text-white-50">Now check the photo</h4>
          <p className="text-center text-white-50">
            Make sure the photo is not blurry or obscured by any harsh light.
          </p>
          <div className="flex gap-4">
            <button
              className="h-10 text-base font-medium text-center text-white bg-black rounded-8 w-38 border-[1px] border-solid border-white"
              type="button"
              onClick={onRetry}
            >
              {retryButtonText.get(imageUploadState.journey ?? ImageUploadJourney.UPLOAD)}
            </button>
            <button
              className="h-10 text-base font-medium text-center text-white bg-red-600 rounded-8 w-44"
              type="button"
              onClick={onConfirm}
            >
              Confirm
            </button>
          </div>
        </section>
      )}
    </dialog>
  );
}
